import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { ZodObject, ZodRawShape } from 'zod';

interface UseCustomQueryParams<T> {
  queryKey: any[];
  fetchFunction: (params?: any) => Promise<T[] | null>;
  isEntity: (obj: any) => obj is T;
  functionName?: string;
  params?: string | any;
  enabled: boolean;
  displayNotification?: boolean;
  validationSchema?: ZodObject<ZodRawShape>;
  refetchInterval?: number;
  staleTime?: number;
  gcTime?: number;
  refetchOnWindowFocus?: boolean;
  refetchOnMount?: boolean | 'always';
}

/**
 * Custom hook for handling list queries with error notifications.
 *
 * @param queryKey - The key for the query.
 * @param fetchFunction - The function to fetch data for the query.
 * @param isEntity - Function to check if an object is of type T.
 * @param functionName - The name of the function.
 * @param params - Parameters for the fetch function.
 * @param enabled - Flag to enable/disable the query.
 * @param displayNotification - Flag to display error notifications (default is true).
 *
 * @returns The result of the query with data of type T or null, and potential error of type Error.
 */

export const useListQuery = <T extends { id: string }>({
  queryKey,
  fetchFunction,
  isEntity,
  params,
  enabled,
  validationSchema,
  refetchInterval = -1,
  staleTime,
  gcTime,
  refetchOnWindowFocus,
  refetchOnMount,
}: UseCustomQueryParams<T>): UseQueryResult<T[], Error> => {
  const key = [...queryKey].filter((item) => item !== undefined);
  const invalidData: T[] = [];
  const invalidDataIds: string[] = [];
  const validationErrors: any[] = [];
  return useQuery<T[], Error>({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: key,
    queryFn: async () => {
      // get the data from the list function as json object of type T or []
      const data = params ? await fetchFunction(params) : await fetchFunction();

      for (const entity of data) {
        if (!isEntity(entity)) {
          if (entity && 'id' in entity) {
            invalidData.push(entity);
            invalidDataIds.push((entity as { id: string }).id);
            if (validationSchema) {
              validationErrors.push({
                id: (entity as { id: string }).id,
                errors: validationSchema?.safeParse(entity).error,
              });
            }
          }
        }
      }

      return data;
    },
    refetchInterval: refetchInterval > 0 ? refetchInterval : false,
    enabled: enabled,
    staleTime,
    gcTime,
    refetchOnWindowFocus,
    refetchOnMount,
    retry: false,
  });
};
